import { useNavigate, useParams } from "react-router-dom";
import Button from "../../components/atoms/button";
import AppBar from "../../components/atoms/appbar";
import * as React from "react";
import CompetitionDrawer from "../../components/atoms/drawer/CompetitionDrawer";
import { useQuery } from "@tanstack/react-query";
import {
  useTournamentCalendarList,
  useTournamentDetail,
} from "../../@apis/hooks/useTournament";
import { formatDate, formatDateRange } from "../../lib/utils/dateUtils";
import type { TournamentDetailResponse } from "../../@apis/dto/response/torunament.response";
import { useModalStore } from "../../lib/store/useModalStore";
import { staticURL } from "../../lib/utils/imageUtils";
import { useAccessStore } from "../../lib/store/useAccessStore";
import { useDeviceStore } from "../../lib/store/useDeviceStore";
import dayjs from "dayjs";
import DepositAccountDrawer from "../../components/atoms/drawer/DepositAccountDrawer.tsx";
import ConfirmDrawer from "../../components/atoms/drawer/ConfirmDrawer.tsx";
import { cn } from "../../lib/utils/cn.ts";
import { saveScrollPosition } from "../../hooks/useMainScrollRestoration.ts";
// 접수 대기열 배포 보류
// import { useApplyQueueGate } from "../../hooks/useApplyQueueGate.ts";
// import TournamentQueueDialog from "../../components/atoms/dialog/TournamentQueueDialog.tsx";
import {
  resolveTournamentListStatus,
  unavailableScheduleMessage,
} from "../../lib/utils/tournamentUtils.ts";
import { useMyInfoStore } from "../../lib/store/useMyInfoStore";
import type { TournamentResponse } from "../../@apis/dto/response/torunament.response";
import { requestPhoneCall } from "../../lib/bridge";

// 서울(02)은 지역번호 2자리, 그 외는 3자리로 끊는다.
function formatCallbotPhone(phone: string) {
  const digits = phone.replace(/\D/g, "");
  if (digits.startsWith("02")) {
    if (digits.length === 9) {
      return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
    }
    if (digits.length === 10) {
      return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
    }
  }
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return digits || phone;
}

// 상단 정보 카드 한 줄 (라벨 회색 / 값 검정)
function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-[8px] items-start">
      <p className="min-w-[61px] shrink-0 text-[#8e8e93]">{label}</p>
      <p className="flex-1 min-w-0 text-black break-keep whitespace-pre-line [overflow-wrap:anywhere]">
        {value}
      </p>
    </div>
  );
}

export default function CompetitionDetail() {
  const { openModal } = useModalStore();
  const { access } = useAccessStore();
  const { deviceType } = useDeviceStore();
  const [open, setOpen] = React.useState(false);
  const [openDepositDrawer, setOpenDepositDrawer] = React.useState(false);
  const [confirmDrawer, setConfirmDrawer] = React.useState(false);
  const bottomBarRef = React.useRef<HTMLDivElement>(null);
  const [bottomBarHeight, setBottomBarHeight] = React.useState(0);
  const navigate = useNavigate();

  const { id } = useParams();

  const {
    data: tournamentInfo,
    refetch,
    isError,
    error,
  } = useQuery({
    ...useTournamentDetail(id as string),
    enabled: !!id,
  });
  const { data, success } = tournamentInfo || {};

  const { data: calendarListRes } = useQuery({
    ...useTournamentCalendarList(id as string),
    enabled: !!id,
  });
  const calendarList = calendarListRes?.data ?? [];

  const scrollKey = `competition_detail_${id}`;

  // 접수 대기열 배포 보류 — 되살릴 때 아래 주석을 함께 해제한다
  // 접수 대기는 이 화면에서 끝낸다 — 순서가 되면 그때 접수 화면으로 넘긴다
  // const exitQueue = React.useCallback(
  //   (title: string, content?: string) => {
  //     openModal("ALERT_DIALOG", { title, content });
  //     refetch();
  //   },
  //   [openModal, refetch],
  // );
  // const {
  //   gate: queueGate,
  //   waitingAhead,
  //   capacityFull,
  //   enter: enterQueue,
  //   cancel: cancelQueue,
  // } = useApplyQueueGate({ id, onExit: exitQueue });

  // React.useEffect(() => {
  //   if (queueGate !== "pass") return;
  //   saveScrollPosition(scrollKey);
  //   navigate(`/competition/${id}/apply`);
  // }, [queueGate, id, navigate, scrollKey]);

  // 접수 화면에서 뒤로 돌아왔을 때 이전 스크롤 위치 복원.
  // 상세 본문(이미지/HTML)이 늦게 그려지므로 목표 위치에 닿을 때까지 재시도한다.
  React.useEffect(() => {
    const saved = sessionStorage.getItem(`scroll_${scrollKey}`);
    if (saved === null) return;
    sessionStorage.removeItem(`scroll_${scrollKey}`);

    const mainEl = document.querySelector("main");
    if (!mainEl) return;

    const target = Number(saved);
    const deadline = Date.now() + 1500;
    let raf = 0;
    const restore = () => {
      mainEl.scrollTop = target;
      if (Math.abs(mainEl.scrollTop - target) > 1 && Date.now() < deadline) {
        raf = requestAnimationFrame(restore);
      }
    };
    raf = requestAnimationFrame(restore);

    return () => cancelAnimationFrame(raf);
  }, [scrollKey]);

  React.useLayoutEffect(() => {
    const el = bottomBarRef.current;
    if (!el) return;
    const update = () => setBottomBarHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [data]);

  const myStampLabel = data?.tournamentInfo?.myStampLabel;

  // 버튼 노출 조건
  // 재접수 유저는 reapply(시작/종료), 첫 접수 유저는 apply(시작/종료) 기준으로 처리
  const effectiveApplyStartTimestamp = data?.tournamentInfo?.isReapply
    ? data?.tournamentInfo?.reapplyStartDtTimestamp
    : data?.tournamentInfo?.applyStartDtTimestamp;
  const effectiveApplyEndTimestamp = data?.tournamentInfo?.isReapply
    ? data?.tournamentInfo?.reapplyEndDtTimestamp
    : data?.tournamentInfo?.applyEndDtTimestamp;

  // 접수(재접수) 기간을 dayjs 로 변환해 현재 시점과 비교한다.
  const now = dayjs();
  const applyStart =
    effectiveApplyStartTimestamp != null
      ? dayjs(effectiveApplyStartTimestamp)
      : null;
  const applyEnd =
    effectiveApplyEndTimestamp != null
      ? dayjs(effectiveApplyEndTimestamp)
      : null;

  // 접수 시작 전: 현재 시점이 시작일시 이전
  const isBeforeApplyStart = applyStart != null && now.isBefore(applyStart);
  // 접수 기간중: 시작일시 ~ 종료일시 사이 (접수하기 버튼 노출)
  const isApplyOpen =
    applyStart != null &&
    applyEnd != null &&
    !now.isBefore(applyStart) &&
    !now.isAfter(applyEnd);
  // 접수 마감: 현재 시점이 종료일시 초과
  const isApplyClosedForNew = applyEnd != null && now.isAfter(applyEnd);

  // 접수 시작 전이면 시작일시 표기, 접수 기간중이면 '접수하기'
  const applyButtonLabel =
    isBeforeApplyStart && effectiveApplyStartTimestamp != null
      ? `${formatDate(new Date(effectiveApplyStartTimestamp), "YYYY.MM.DD(ddd) HH:mm")} 접수 시작`
      : "접수하기";

  const showApprovalWaitButton =
    myStampLabel === "승인대기" || myStampLabel === "미입금 마감";
  const showDepositWaitButtons = myStampLabel === "결제대기";
  const showConfirmButtons =
    myStampLabel === "접수완료" || myStampLabel === "참가완료";
  const showApplyButton = !myStampLabel && !isApplyClosedForNew;
  const hasBottomActions =
    showApplyButton ||
    showApprovalWaitButton ||
    showDepositWaitButtons ||
    showConfirmButtons;

  const applyHander = (startIso: number, endIso: number) => {
    // 온라인 결제 대회는 결제 모듈이 앱 전용이라 웹에서는 접수 불가
    if (
      data?.tournamentInfo?.paymentMethod === "online" &&
      deviceType === "web"
    ) {
      openModal("ALERT_DIALOG", {
        title: "접수 불가",
        content: "접수는 보이스파크 앱에서 가능합니다.",
      });
      return;
    }

    const now = dayjs();
    const startTime = dayjs(startIso);
    const endTime = dayjs(endIso);

    if (now.isBefore(startTime)) {
      openModal("ALERT_DIALOG", {
        title: "아직 접수 시작 전입니다.",
        content: `접수 시작은 ${startTime.format("YYYY.MM.DD (ddd) HH:mm")}입니다.`,
      });
      return;
    }

    if (now.isAfter(endTime)) {
      openModal("ALERT_DIALOG", {
        title: "접수가 마감되었습니다.",
        content: `접수 마감일은 ${endTime.format("M월 D일 A h시")}이었습니다.`,
      });
      return;
    }

    if ((data?.tournamentInfo.calendarList?.length ?? 0) === 0) {
      openModal("ALERT_DIALOG", {
        title: "접수할 수 없습니다",
        content: unavailableScheduleMessage(
          useMyInfoStore.getState().myInfo.gender,
        ),
      });
      return;
    }

    // 접수 대기열 배포 보류 — 되살릴 때 enterQueue()로 바꾼다
    // enterQueue();
    saveScrollPosition(scrollKey);
    navigate(`/competition/${id}/apply`);
  };

  React.useEffect(() => {
    if (!isError) return;
    if (success) return;

    // API 에러에서 "없는 경기" 케이스를 표시
    const err = (error as any)?.response?.data as
      | { error?: { code?: string | null; message?: string | null } }
      | undefined;

    const message = err?.error?.message ?? "경기를 찾을 수 없습니다.";
    openModal("ALERT_DIALOG", {
      title: "에러",
      content: message,
      onConfirm: () => navigate(-1),
    });
  }, [isError, success, error]);

  const status = data?.tournamentInfo
    ? resolveTournamentListStatus(
      data.tournamentInfo as unknown as TournamentResponse,
    )
    : null;

  const info = data?.tournamentInfo;
  const callbotPhone = info?.callbotPhone?.trim();
  const applyRangeText = info
    ? `${formatDate(
      new Date(info.applyStartDtTimestamp),
      "YYYY.MM.DD (ddd) HH:mm",
    )} ~ ${formatDate(new Date(effectiveApplyEndTimestamp as number), "YYYY.MM.DD (ddd) HH:mm")}`
    : "";

  return (
    <div
      className="min-h-screen bg-white"
      style={{ paddingBottom: bottomBarHeight }}
    >
      <AppBar title="대회 상세" backButton />

      {success && info && (
        <>
          {/* 상단 정보 */}
          <section className="p-[20px] flex flex-col gap-[20px]">
            <div className="flex flex-col gap-[12px]">
              {status && (
                <div
                  className={cn(
                    "py-[2.5px] w-fit px-[10px] rounded-[5px] flex items-center justify-center gap-[5px] text-sm font-medium whitespace-nowrap",
                    status.badgeClassName,
                  )}
                >
                  {status.hasCheck && (
                    <svg
                      width="1rem"
                      height="1rem"
                      viewBox="0 0 16 16"
                      fill="none"
                      className="shrink-0"
                    >
                      <path
                        d="M13.3307 4L5.9974 11.3333L2.66406 8"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                  {status.label}
                </div>
              )}
              <p className="text-base font-semibold text-black break-keep">
                {info.name}
              </p>
            </div>

            <div className="flex flex-col gap-[8px] text-sm font-medium">
              <InfoRow label="접수기간" value={applyRangeText} />
              <InfoRow
                label="대회기간"
                value={formatDateRange(
                  info.startDt,
                  info.endDt,
                  "YYYY.MM.DD (ddd)",
                )}
              />
              <InfoRow label="대회장소" value={info.golfParkName} />
              <InfoRow label="대회주소" value={info.address ?? "-"} />
              {/* TODO: 참가인원 API 미제공 → 하드코딩 */}
              {/* <InfoRow label="참가인원" value="400명" /> */}
              <InfoRow label="접수방법" value="보이스파크 앱에서 접수" />
              {callbotPhone && (
                <InfoRow
                  label="AI콜봇"
                  value={
                    <button
                      type="button"
                      className="text-left"
                      onClick={() => requestPhoneCall(callbotPhone)}
                    >
                      {`${formatCallbotPhone(callbotPhone)} (앱 설치·접수 방법 안내 전용)`}
                    </button>
                  }
                />
              )}
              {info.depositConfirmInfo && (
                <InfoRow label="대회문의" value={info.depositConfirmInfo} />
              )}
            </div>
          </section>

          <div className="h-[5px] w-full bg-[#f2f2f7]" />

          {/* 대회 상세 요강 */}
          <section className="p-[20px] flex flex-col gap-[16px]">
            {isApplyOpen && calendarList.length > 0 ? (
              <Button
                className="w-full font-normal"
                variant={"red"}
                label="실시간 접수 현황 보기"
                onClick={() => {
                  saveScrollPosition(scrollKey);
                  navigate(`/competition/${id}/status`);
                }}
              />
            ) : // <Button
              //   className="w-full font-bold"
              //   variant={"white"}
              //   label="대회 순위 보기"
              //   onClick={() => navigate(`/competition/${id}/ranking`)}
              // />
              null}

            <div className="border-l-[3px] border-[#8e8e93] px-[12px]">
              <p className="text-base font-semibold text-black">
                대회 상세 요강
              </p>
            </div>

            {info.thumbnailUrl && (
              <img
                className="w-full block"
                src={staticURL(info.thumbnailUrl)}
                alt=""
              />
            )}

            {info.detailImageUrl && (
              <img
                className="w-full block"
                src={staticURL(info.detailImageUrl)}
                alt=""
              />
            )}

            {/* 상세 정보 HTML (있을 때만 노출) */}
            {info.detailBottomHtml && (
              <div
                className="break-keep [&_img]:max-w-full"
                dangerouslySetInnerHTML={{ __html: info.detailBottomHtml }}
              />
            )}
          </section>
        </>
      )}

      {/* 끝 */}

      <div
        ref={bottomBarRef}
        className={cn(
          // items-stretch: 라벨이 두 줄이 되는 버튼이 있으면 나머지도 같은 높이로 맞춘다
          "bg-white p-[15px] fixed max-w-[440px] w-full bottom-0 flex-row-between items-stretch gap-[10px] z-50",
          { "p-0": !hasBottomActions },
        )}
      >
        {/* 미입금 마감: 재접수 제한 안내를 버튼 위 말풍선으로 노출 */}
        {myStampLabel === "미입금 마감" && (
          <div className="absolute left-0 right-0 bottom-full px-[15px] pb-[8px]">
            <div className="relative mx-auto w-fit max-w-full rounded-[10px] bg-[#8e8e93] px-[12px] py-[8px] text-center text-xs font-medium text-white break-keep">
              미입금 마감 후 약 30분 동안은 재접수가 불가능합니다.
              <span className="absolute left-1/2 top-full block h-0 w-0 -translate-x-1/2 border-x-[6px] border-t-[6px] border-x-transparent border-t-[#8e8e93]" />
            </div>
          </div>
        )}

        {success && showApplyButton && (
          <Button
            onClick={() =>
              access.accessToken
                ? applyHander(
                  effectiveApplyStartTimestamp as number,
                  effectiveApplyEndTimestamp as number,
                )
                : openModal("CONFIRM_DIALOG", {
                  title: "로그인이 필요합니다.",
                  content:
                    "이 기능은 로그인 후 이용할 수 있습니다.\n 로그인하시겠습니까?",
                  onClick: () => navigate("/login", { replace: true }),
                })
            }
            className="w-full font-normal"
            variant={"red"}
            // 접수 대기열 배포 보류 — disabled={queueGate !== "idle"}
            label={applyButtonLabel}
          />
        )}

        {/* 승인대기: 접수내역 보기 버튼만 노출 */}
        {showApprovalWaitButton && (
          <Button
            onClick={() =>
              navigate(
                `/setting/tournament-history/${id}/${data?.tournamentInfo.apply?.id}`,
              )
            }
            className="w-full font-normal"
            variant={"black"}
            label="접수내역 보기"
          />
        )}

        {/* 결제대기(가상계좌/무통장입금): 입금계좌 확인 + 접수내역 보기 */}
        {showDepositWaitButtons && (
          <>
            <Button
              onClick={() => setOpenDepositDrawer(true)}
              className="w-full font-normal whitespace-pre-line break-keep"
              variant={"red"}
              label="입금계좌 확인"
            />
            <Button
              onClick={() =>
                navigate(
                  `/setting/tournament-history/${id}/${data?.tournamentInfo.apply?.id}`,
                )
              }
              className="w-full font-normal whitespace-pre-line break-keep"
              variant={"black"}
              label="접수내역 보기"
            />
          </>
        )}
        {/* 접수완료 || 참가완료: 확인서 보기 + 접수내역 보기 */}
        {showConfirmButtons && (
          <>
            <Button
              onClick={() => setConfirmDrawer(true)}
              className="w-full font-normal whitespace-pre-line break-keep"
              variant={"red"}
              label="확인서 보기"
            />
            <Button
              onClick={() =>
                navigate(
                  `/setting/tournament-history/${id}/${data?.tournamentInfo.apply?.id}`,
                )
              }
              className="w-full font-normal whitespace-pre-line break-keep"
              variant={"black"}
              label="접수내역 보기"
            />
          </>
        )}
      </div>

      {open && (
        <CompetitionDrawer
          id={id as string}
          type="edit"
          open={open}
          item={data as TournamentDetailResponse}
          onClose={() => {
            setOpen(false);
            refetch();
          }}
          calendarList={data?.tournamentInfo.calendarList || []}
        />
      )}

      {openDepositDrawer && (
        <DepositAccountDrawer
          open={openDepositDrawer}
          onClose={() => setOpenDepositDrawer(false)}
          item={data as TournamentDetailResponse}
        />
      )}

      {confirmDrawer && (
        <ConfirmDrawer
          open={confirmDrawer}
          onClose={() => setConfirmDrawer(false)}
          item={data as TournamentDetailResponse}
        />
      )}

      {/* 접수 대기열 배포 보류
      {queueGate === "waiting" && (
        <TournamentQueueDialog
          waitingAhead={waitingAhead}
          capacityFull={capacityFull}
          onCancel={cancelQueue}
        />
      )} */}
    </div>
  );
}
